import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Spin } from 'antd'
import styled from 'styled-components'
import { Doughnut, Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from 'chart.js'
import { fetchAssessmentChartsData } from '../actions'

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement)

const ChartContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 0px;
  border: 1px solid #f0f0f0;
  /* Fixed height so all cards align exactly */
  min-height: 415px;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`

const ChartTitle = styled.h3`
  margin-bottom: 8px;
  color: #1a1a1a;
  font-weight: 600;
  font-size: 24px;
  text-align: center;
`

const ChartWrapper = styled.div`
  height: 250px;
  width: 100%;
  max-width: 250px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 15px;
`

const SelectedInfo = styled.div`
  margin: 12px auto 0;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
  font-size: 16px;
  color: #495057;
  min-height: 60px; /* Reserve consistent space across cards (2 lines) */
  width: calc(100% - 16px);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #f0f0f0;
  box-sizing: border-box;
`

const ClearButton = styled.button`
  background: #f0f0f0;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  margin-left: 8px;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  
  &:hover {
    background: #e0e0e0;
  }
`

// Subtle text-like clear link absolutely positioned at the top-right of the footer
const ClearLink = styled.button`
  position: absolute;
  top: -2px; /* sits slightly above the note edge like in the design */
  right: 12px;
  background: transparent;
  border: none;
  color: #ff4d4f;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #cf1322;
    text-decoration: underline;
  }
`

const ToggleButton = styled.button`
  background: #1890ff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  cursor: pointer;
  font-size: 14px;
  color: white;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #40a9ff;
    transform: translateY(-1px);
  }
  
  &:active {
    background: #e6f7ff;
  }
  
  &:focus {
    outline: none;
  }
`

const RetryButton = styled.button`
  background: #52c41a;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  cursor: pointer;
  font-size: 14px;
  color: white;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px auto;
  transition: all 0.3s ease;
  
  &:hover {
    background: #73d13d;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`

const ErrorContainer = styled.div`
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  text-align: center;
  color: #cf1322;
`

const LineChartContainer = styled.div`
  position: relative;
  height: 270px;
  width: 100%;
`

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
  margin-bottom: 4px;
  /* Reserve enough space so legends don't push card height */
  min-height: 32px;
`

// Absolute-positioned next button so it sits above the canvas and is always clickable
const NextButton = styled.button`
  position: absolute;
  right: -6px;
  bottom: 25px;
  z-index: 5; /* Above chart canvas */
  background: #fff;
  border: 1px solid #fff;
  border-radius: 12px;
  padding: 2px 8px;
  line-height: 1;
  font-size: 14px;
  font-weight: bold;
  color: #0000FF;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  user-select: none;
  transition: box-shadow 0.15s ease, transform 0.05s ease;
  &:hover { box-shadow: 0 2px 6px rgba(0,0,0,0.12); }
  &:active { transform: scale(0.98); }
`

const PrevButton = styled.button`
  position: absolute;
  left: -6px;
  bottom: 25px;
  z-index: 5; /* Above chart canvas */
  background: #fff;
  border: 1px solid #fff;
  border-radius: 12px;
  padding: 2px 8px;
  line-height: 1;
  font-size: 14px;
  font-weight: bold;
  color: #0000FF;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  user-select: none;
  transition: box-shadow 0.15s ease, transform 0.05s ease;
  &:hover { box-shadow: 0 2px 6px rgba(0,0,0,0.12); }
  &:active { transform: scale(0.98); }
  ${props => props.disabled ? 'opacity: 0.5; cursor: not-allowed;' : ''}
`

const ScrollContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
  max-width: 100%;
`

const ScrollButton = styled.button`
  background: #1890ff;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 18px;
  font-weight: bold;
  margin: 0 8px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(24, 144, 255, 0.3);
  transition: all 0.2s ease;
  
  &:hover {
    background: #40a9ff;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    background: #d9d9d9;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    opacity: 0.6;
  }
`

const ChartViewport = styled.div`
  height: 100%;
  position: relative;
  width: 100%;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`

const BlurOverlayContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const BlurredChartPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  filter: blur(6px);
  opacity: 0.5;
  pointer-events: none;
`

const DataNotAvailableOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
  border-radius: 8px;
  z-index: 10;
`

const DataNotAvailableText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const DurationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`

const DurationLabel = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 16px;
  white-space: nowrap;
`

const DurationSelect = styled.select`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 16px;
  color: #374151;
  background: white;
  min-width: 150px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #3249d7;
  }
  
  &:focus {
    outline: none;
    border-color: #3249d7;
    box-shadow: 0 0 0 3px rgba(50, 73, 215, 0.15);
  }
`

const BlueInfoBox = styled.div`
  background: #fff;
  border: 1px solid #91d5ff;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 15px auto 0;
  width: calc(100% - 16px);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  line-height: 1.4;
  min-height: 60px; /* Keep footer height stable when no selection */
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  box-sizing: border-box;
`

const InfoIcon = styled.div`
  color: #1890ff;
  font-weight: bold;
  font-size: 16px;
  margin-top: 1px;
  flex-shrink: 0;
`

const InfoText = styled.div`
  flex: 1;
`

const ChartNote = styled.div`
  margin: 12px auto 0;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #91d5ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  line-height: 1.4;
  min-height: 60px; /* Match BlueInfoBox height */
  width: calc(100% - 16px);
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  box-sizing: border-box;
`

// Fixed height footer to prevent chart resizing on selection
const ChartFooter = styled.div`
  margin-top: auto;
  min-height: 80px; /* Fixed height: ClearLink (20px) + SelectedInfo/ChartNote (60px) */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`

const AssessmentCharts = ({
  assessment,
  instituteId,
  passingYear = '',
  studentDataCompleted,
  studentDataSent,
  studentDataPending,
  studentDataInProgress,
  studentDataDroppedOff,
  chartFilters,
  setChartFilters,
  clearChartFilters,
  externalChartData = null, // New prop for injecting data (e.g. from Diagnosis)
  disableFetch = false, // Prop to explicitly disable internal data fetching
  isDiagnosis = false, // Prop to indicate diagnosis context
  disableLineChart = false, // Prop to hide line chart
  scheduleId = null, // Schedule ID for attempt-order mode
  attemptNumber = null // Attempt number for attempt-order mode
}) => {
  const dispatch = useDispatch()
  const [selectedPieChart, setSelectedPieChart] = useState(null)
  const [selectedConsistency, setSelectedConsistency] = useState(null)
  const [selectedLinePoint, setSelectedLinePoint] = useState(null)
  const [lineChartStartIndex, setLineChartStartIndex] = useState(0) // Index for visible window (0 = start from first assessment)
  const [chartsVisible, setChartsVisible] = useState(true) // New state for hide/show toggle

  // Friendly name helpers
  const getFriendlyCefr = (level) => {
    if (!level) return ''
    const map = {
      'A1': 'A1 (Beginner)',
      'A2': 'A2 (Beginner)',
      'B1': 'B1 (Intermediate)',
      'B2': 'B2 (Upper-Intermediate)',
      'C1': 'C1 (Advanced)',
      'C2': 'C2 (Advanced)'
    }
    return map[level] || level
  }

  const getFriendlyAptitude = (level) => {
    if (!level) return ''
    const normLevel = String(level).trim().toLowerCase()

    // Mapping Chart Labels (Beginner, Learner, Advanced, Expert) to Friendly Names
    const map = {
      'beginner': 'Beginner',
      'learner': 'Intermediate',
      'competent': 'Upper Intermediate', // API legacy
      'advanced': 'Advanced',  // Chart "Advanced" -> Friendly "Upper Intermediate"
    }

    // If exact match failing, try case insensitive lookup
    return map[normLevel] || level
  }

  // Get chart data from Redux store with correct state path
  // Get chart data from Redux store with correct state path
  const reduxChartData = useSelector(state => {
    // console.log('🔍 useSelector: Full state keys:', Object.keys(state))
    // console.log('🔍 useSelector: state.SubscribedInstitutes:', state.SubscribedInstitutes)
    // console.log('🔍 useSelector: state.SubscribedInstitutes?.chartData:', state.SubscribedInstitutes?.chartData)
    return state.SubscribedInstitutes?.chartData
  })

  // Use external data if provided, otherwise fallback to Redux
  const chartData = externalChartData || reduxChartData
  const chartLoading = useSelector(state => state.SubscribedInstitutes?.chartLoading || false)
  const chartError = useSelector(state => state.SubscribedInstitutes?.chartError || null)

  // Force re-render when chartData changes
  useEffect(() => {
    console.log('🔄 chartData changed in useEffect:', chartData)
    if (chartData) {
      console.log('✅ Chart data is now available:', chartData)
    } else {
      console.log('❌ Chart data is still null/undefined')
    }
  }, [chartData])

  // Sync selection state with chartFilters changes
  useEffect(() => {
    if (!chartFilters) {
      setSelectedPieChart(null)
      setSelectedConsistency(null)
      return
    }

    // Sync Consistency
    if (chartFilters.consistency) {
      const types = ['high', 'moderate', 'inconsistent']
      const labels = ['High Consistency', 'Moderate Consistency', 'Inconsistent']
      const idx = types.findIndex(t => chartFilters.consistency.toLowerCase().includes(t))
      if (idx !== -1) {
        let count = 0
        try {
          // Safely try to get count, might fail if functions not initialized yet or data missing
          // Rely on closure variable access (available after render)
          const chart = prepareConsistencyChartData()
          count = chart?.datasets?.[0]?.data?.[idx] || 0
        } catch (e) {
          console.warn('Could not derive count for sync:', e)
        }
        setSelectedConsistency({
          type: types[idx],
          label: labels[idx],
          index: idx,
          count: count
        })
      }
    } else {
      setSelectedConsistency(null)
    }

    // Sync Pie Chart (CEFR or Aptitude)
    const level = chartFilters.cefrLevel || chartFilters.aptitudeLevel
    if (level) {
      const cefrLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
      const aptLevels = ['Beginner', 'Learner', 'Competent', 'Advanced']

      let foundIndex = cefrLevels.indexOf(level)
      let count = 0

      if (foundIndex !== -1) {
        let chartDataObj = null;
        try {
          chartDataObj = prepareCefrChartData()
          count = chartDataObj?.datasets?.[0]?.data?.[foundIndex] || 0
        } catch (e) { }
        setSelectedPieChart({ level: level, index: foundIndex, count: count })
      } else {
        foundIndex = aptLevels.indexOf(level)
        if (foundIndex !== -1) {
          let chartDataObj = null;
          try {
            // prepareCefrChartData handles isAptitudeAssessment internally too
            chartDataObj = prepareCefrChartData()
            count = chartDataObj?.datasets?.[0]?.data?.[foundIndex] || 0
          } catch (e) { }
          setSelectedPieChart({ level: level, index: foundIndex, count: count })
        }
      }
    } else {
      setSelectedPieChart(null)
    }

  }, [chartFilters, chartData]) // Add chartData dependency to update counts if data arrives later

  // Fetch chart data when component mounts or assessment changes
  useEffect(() => {
    // The backend APIs expect assessmentInstituteMapID, so we need to find the correct field
    const assessmentInstituteMapID = assessment?.assessmentInstituteMapId ||
      assessment?.assessment_institute_map_id ||
      assessment?.assessmentInstituteMapID ||
      assessment?.assessment_institute_map_ID ||
      assessment?.mapId ||
      assessment?.id ||
      assessment?.assessmentId ||
      assessment?.latestAssessmentInstituteMapId

    // Get assessment type
    const assessmentType = assessment?.type || assessment?.assessmentType || assessment?.assessment_type || ''

    console.log('=== Assessment ID Debug ===')
    console.log('Full assessment object:', assessment)
    console.log('All possible ID fields:')
    console.log('- assessmentInstituteMapId:', assessment?.assessmentInstituteMapId)
    console.log('- assessmentInstituteMapID:', assessment?.assessmentInstituteMapID)
    console.log('- assessment_institute_map_id:', assessment?.assessment_institute_map_id)
    console.log('- mapId:', assessment?.mapId)
    console.log('- id:', assessment?.id)
    console.log('- assessmentId:', assessment?.assessmentId)
    console.log('Selected assessmentInstituteMapID:', assessmentInstituteMapID)
    console.log('Assessment type:', assessmentType)

    if (assessmentInstituteMapID && !externalChartData && !disableFetch) {
      console.log('🚀 Fetching chart data for assessmentInstituteMapID:', assessmentInstituteMapID)
      console.log('🔍 Assessment type:', assessmentType.toLowerCase())

      // Clear existing chart data first to prevent showing stale data
      dispatch({
        type: 'FETCH_ASSESSMENT_CHARTS_REQUEST'
      })

      // Then fetch new data with assessment type
      dispatch(fetchAssessmentChartsData(assessmentInstituteMapID, assessmentType, instituteId || '', passingYear || '', isDiagnosis, scheduleId, attemptNumber))
    } else {
      console.log('❌ No valid assessmentInstituteMapID found, cannot fetch chart data')
      console.log('Available fields:', Object.keys(assessment || {}))
    }
  }, [assessment, dispatch, instituteId, passingYear, isDiagnosis])

  // Retry function for failed API calls
  const retryChartData = () => {
    const assessmentInstituteMapID = assessment?.assessmentInstituteMapId ||
      assessment?.assessment_institute_map_id ||
      assessment?.assessmentInstituteMapID ||
      assessment?.assessment_institute_map_ID ||
      assessment?.mapId ||
      assessment?.id ||
      assessment?.assessmentId

    const assessmentType = assessment?.type || assessment?.assessmentType || assessment?.assessment_type || ''

    if (assessmentInstituteMapID) {
      console.log(' Retrying chart data fetch...')
      dispatch(fetchAssessmentChartsData(assessmentInstituteMapID, assessmentType, instituteId || '', passingYear || '', isDiagnosis, scheduleId, attemptNumber))
    }
  }

  // Chart interaction handlers for student data filtering
  const handleCefrClick = (event, elements) => {
    console.log(' CEFR chart click triggered!', { event, elements })

    if (elements && elements.length > 0) {
      const clickedIndex = elements[0].index

      // Determine levels based on assessment type
      let levels, clickedLevel
      if (isCommunicationAssessment) {
        levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
        clickedLevel = levels[clickedIndex]
      } else if (isAptitudeAssessment) {
        levels = ['Beginner', 'Learner', 'Competent', 'Advanced']
        clickedLevel = levels[clickedIndex]
      }

      console.log(' Chart clicked:', clickedLevel, 'at index:', clickedIndex)

      // Derive count from prepared chart dataset to avoid API shape assumptions
      let derivedCount = 0
      try {
        const preparedPie = prepareCefrChartData()
        derivedCount = preparedPie?.datasets?.[0]?.data?.[clickedIndex] ?? 0
      } catch (e) {
        console.warn('Failed to derive pie count from prepared data:', e)
      }

      // Set selected state for visual feedback
      setSelectedPieChart({
        level: clickedLevel,
        index: clickedIndex,
        count: derivedCount,
        percentage: null
      })

      // Set chart filters for student data filtering
      if (setChartFilters) {
        console.log(' setChartFilters function exists:', typeof setChartFilters)
        if (isCommunicationAssessment) {
          console.log(' Setting CEFR chart filter:', clickedLevel)
          setChartFilters({
            cefrLevel: clickedLevel, // Changed from cefr to cefrLevel to match AssessmentDetails
            assessmentType: 'communication'
          })
          console.log(' Chart filters set for CEFR:', clickedLevel)
        } else if (isAptitudeAssessment) {
          console.log(' Setting Aptitude competency filter:', clickedLevel)
          setChartFilters({
            aptitudeLevel: clickedLevel,
            assessmentType: 'aptitude'
          })
          console.log(' Chart filters set for Aptitude:', clickedLevel)
        }
      } else {
        console.error(' setChartFilters function not available!')
      }
    } else {
      console.log(' No elements found in CEFR chart click')
    }
  }

  const handleConsistencyClick = (event, elements) => {
    console.log(' Consistency chart click triggered!', { event, elements })

    if (elements && elements.length > 0) {
      const clickedIndex = elements[0].index
      const consistencyLevels = ['High Consistency', 'Moderate Consistency', 'Inconsistent']
      // Update values to match backend expectations (TpoDashBoard.js levelMapFunc)
      const consistencyValues = ['high', 'moderate', 'inconsistent']
      const clickedLevel = consistencyLevels[clickedIndex]
      const clickedValue = consistencyValues[clickedIndex]

      console.log('🎯 Consistency chart clicked:', clickedLevel, 'at index:', clickedIndex)

      // Set selected state for visual feedback using prepared dataset values
      let derivedCount = 0
      try {
        const preparedConsistency = prepareConsistencyChartData()
        derivedCount = preparedConsistency?.datasets?.[0]?.data?.[clickedIndex] ?? 0
      } catch (e) {
        console.warn('Failed to derive consistency count from prepared data:', e)
      }

      setSelectedConsistency({
        label: clickedLevel,
        type: clickedValue,
        index: clickedIndex,
        count: derivedCount,
        percentage: null
      })

      // Set chart filters for student data filtering
      if (setChartFilters) {
        const assessmentType = assessment?.type || assessment?.assessmentType || assessment?.assessment_type || ''
        setChartFilters({
          consistency: clickedValue,
          assessmentType: assessmentType.toLowerCase()
        })
        console.log('🔧 Chart filters set for Consistency:', clickedValue)
      }
    } else {
      console.log('❌ No elements found in Consistency chart click')
    }
  }

  const handleChartFilter = (filterType, filterValue) => {
    const assessmentType = (assessment?.type || assessment?.assessmentType || '').toLowerCase();
    console.log(`🔧 Applying filter: { ${filterType}: '${filterValue}', assessmentType: '${assessmentType}' }`);
    if (setChartFilters) {
      setChartFilters({
        [filterType]: filterValue,
        assessmentType: assessmentType
      });
    }
  };

  // Separate clear handlers for each chart to avoid clearing other charts' filters
  const handleClearPieChartFilter = () => {
    console.log('🧹 Clearing pie chart (CEFR/Competency) filter')
    setSelectedPieChart(null)

    // Only clear the cefrLevel or aptitudeLevel filter, keep other filters
    if (setChartFilters) {
      // Reducer merges payload with state, so we must explicitly set keys to null
      setChartFilters({
        cefrLevel: null,
        aptitudeLevel: null
      })
    }
  }

  const handleClearConsistencyFilter = () => {
    console.log('🧹 Clearing consistency chart filter')
    setSelectedConsistency(null)

    // Only clear the consistency filter, keep other filters
    if (setChartFilters) {
      // Reducer merges payload with state, so we must explicitly set keys to null
      setChartFilters({
        consistency: null
      })
    }
  }

  const handleClearLinePointFilter = () => {
    console.log('🧹 Clearing line chart filter')
    setSelectedLinePoint(null)
    // Line chart typically doesn't set filters that affect student list
    // But if it does, clear only line-related filters here
  }

  const handleClearChartFilters = () => {
    console.log('🧹 Clearing ALL chart filters and selections')

    // Clear visual selections
    setSelectedPieChart(null)
    setSelectedConsistency(null)
    setSelectedLinePoint(null)

    // Clear chart filters for student data
    if (clearChartFilters) {
      clearChartFilters()
      console.log('✅ All chart filters cleared')
    }
  }

  // Chart options for pie charts (Doughnut charts)


  // Debug logging to understand data structure
  console.log('=== AssessmentCharts Debug ===')
  console.log('chartData from Redux:', chartData)
  console.log('chartLoading:', chartLoading)
  console.log('chartError:', chartError)
  console.log('assessment:', assessment)

  // Check if chart data is actually changing between assessments
  console.log('=== Chart Data Change Detection ===')
  console.log('Current assessment ID:', assessment?.assessmentInstituteMapId || assessment?.id)
  console.log('Chart data timestamp:', chartData?.timestamp || 'No timestamp')
  console.log('CEFR data hash:', chartData?.cefrData ? JSON.stringify(chartData.cefrData).substring(0, 100) : 'No CEFR data')
  console.log('Consistency data hash:', chartData?.consistencyData ? JSON.stringify(chartData.consistencyData).substring(0, 100) : 'No consistency data')

  // Check if we have any student data to display charts
  const hasStudentData = studentDataCompleted?.length > 0 ||
    studentDataSent?.length > 0 ||
    studentDataPending?.length > 0 ||
    studentDataInProgress?.length > 0 ||
    studentDataDroppedOff?.length > 0

  console.log('hasStudentData:', hasStudentData)

  // Determine assessment type
  const assessmentType = (assessment?.type || assessment?.assessmentType || '').toLowerCase()
  const isCommunicationAssessment = assessmentType === 'communication'
  const isAptitudeAssessment = assessmentType === 'aptitude'

  console.log('Assessment type:', assessmentType)
  console.log('Is Communication Assessment:', isCommunicationAssessment)
  console.log('Is Aptitude Assessment:', isAptitudeAssessment)

  // Helper: Map numeric score to aptitude level string expected by filters
  // Note: Thresholds can be adjusted if product logic differs
  const mapScoreToAptitudeLevel = (score) => {
    const s = Number(score)
    if (!Number.isFinite(s)) return null
    if (s < 25) return 'Beginner'
    if (s < 50) return 'Learner'
    if (s < 75) return 'Advanced'
    return 'Expert'
  }

  // Always try to render charts if we have an assessment, even without student data
  // This will help us see if the issue is with data availability or chart rendering
  if (!assessment) {
    console.log('No assessment provided, not rendering charts')
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <h3>No Assessment Selected</h3>
        <p>Please select an assessment to view charts</p>
      </div>
    )
  }

  // Show message if assessment type is not supported
  if (!isCommunicationAssessment && !isAptitudeAssessment) {
    console.log('Unsupported assessment type:', assessmentType)
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <h3>Charts Not Available</h3>
        <p>Charts are only available for Communication and Aptitude assessments</p>
        <p>Current assessment type: {assessmentType || 'Unknown'}</p>
      </div>
    )
  }

  // Show error state with retry button if charts failed to load
  if (chartError && !chartLoading) {
    return (
      <div>
        <ToggleButton onClick={() => setChartsVisible(!chartsVisible)}>
          {chartsVisible ? '🔼' : '🔽'} {chartsVisible ? 'Hide' : 'Show'} Assessment Charts
        </ToggleButton>

        {chartsVisible && (
          <ErrorContainer>
            <h3>⚠️ Failed to Load Charts</h3>
            <p>There was an error loading the assessment charts:</p>
            <p style={{ fontStyle: 'italic', margin: '10px 0' }}>{chartError}</p>
            <RetryButton onClick={retryChartData}>
              🔄 Retry Loading Charts
            </RetryButton>
          </ErrorContainer>
        )}
      </div>
    )
  }

  const prepareCefrChartData = () => {
    if (isCommunicationAssessment) {
      console.log('Preparing CEFR chart data from API...')
      console.log('Current chartData:', chartData)
      console.log('Assessment type for CEFR:', assessmentType)
      console.log('Current assessment ID:', assessment?.assessmentInstituteMapId || assessment?.id)

      // Use API data if available - NO MOCK DATA
      if (!chartData || !chartData.cefrData) {
        console.log('❌ No CEFR API data available - returning empty chart structure')
        return {
          labels: [],
          datasets: [{
            data: [],
            backgroundColor: [],
            borderWidth: 0
          }]
        }
      }

      const cefrApiData = chartData.cefrData
      console.log('✅ Using real CEFR API data:', cefrApiData)
      console.log('🔍 API data for assessment:', chartData.assessmentId)

      return prepareCefrDataFromResponse(cefrApiData)
    } else if (isAptitudeAssessment) {
      console.log('Preparing Competency chart data from API...')
      console.log('Current chartData:', chartData)
      console.log('Assessment type for Competency:', assessmentType)
      console.log('Current assessment ID:', assessment?.assessmentInstituteMapId || assessment?.id)

      // Use Aptitude Pie Chart API data for competency distribution
      if (!chartData || !chartData.aptitudePieData) {
        console.log('❌ No Competency API data available - returning empty chart structure')
        return {
          labels: [],
          datasets: [{
            data: [],
            backgroundColor: [],
            borderWidth: 0
          }]
        }
      }

      const competencyApiData = chartData.aptitudePieData
      console.log('✅ Using real Competency API data:', competencyApiData)
      console.log('🔍 API data for assessment:', chartData.assessmentId)

      return prepareCompetencyDataFromResponse(competencyApiData)
    }

    return null
  }

  const prepareCefrDataFromResponse = (apiData) => {
    const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
    // New blue color scheme
    const colors = ['#1a48ae', '#4a6ae4', '#6c96f0', '#3d91ff', '#62a1d5', '#aedff7']

    // Handle different possible API response formats
    let chartDataValues = []

    if (apiData.data && Array.isArray(apiData.data)) {
      // Format: { data: [{ level: 'A1', count: X }, ...] }
      chartDataValues = levels.map(level => {
        const levelData = apiData.data.find(item =>
          item.level === level || item.cefrLevel === level || item.name === level
        )
        return levelData?.count || levelData?.value || levelData?.students || 0
      })
    } else if (typeof apiData === 'object') {
      // Format: { A1: { count: X }, A2: { count: Y }, ... } or { A1: X, A2: Y, ... }
      chartDataValues = levels.map(level => {
        const levelData = apiData[level] || apiData[level.toLowerCase()]
        if (typeof levelData === 'object') {
          return levelData?.count || levelData?.value || levelData?.students || 0
        } else if (typeof levelData === 'number') {
          return levelData
        }
        return 0
      })
    } else {
      console.log('Unknown CEFR API data format:', apiData)
      return null
    }

    const totalStudents = chartDataValues.reduce((sum, count) => sum + count, 0)

    if (totalStudents === 0) {
      console.log('No CEFR data in API response, returning null')
      return null
    }

    const labels = levels.map((level, index) => {
      return level
    })

    console.log('CEFR chart data prepared:', { chartDataValues, labels, totalStudents })

    return {
      labels,
      datasets: [{
        data: chartDataValues,
        backgroundColor: colors,
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverBorderWidth: 2,
        hoverBorderColor: '#1a1a1a',
        // Highlight selected segment with darker border
        ...(selectedPieChart && {
          borderColor: colors.map((color, index) =>
            levels[index] === selectedPieChart.level ? '#1a1a1a' : '#ffffff'
          )
        })
      }]
    }
  }

  const prepareCompetencyDataFromResponse = (apiData) => {
    const competencyLevels = ['Beginner', 'Learner', 'Competent', 'Advanced']
    // New blue color scheme
    const colors = ['#1a48ae', '#4a6ae4', '#6c96f0', '#3d91ff']

    // Extract data from API response - handle different possible response formats
    let chartDataValues = []

    if (apiData.data && Array.isArray(apiData.data)) {
      // Format: { data: [{ level: 'Beginner', count: X }, ...] }
      chartDataValues = competencyLevels.map(level => {
        const levelData = apiData.data.find(item =>
          item.level === level || item.competencyLevel === level || item.name === level
        )
        return levelData?.count || levelData?.value || levelData?.students || 0
      })
    } else if (typeof apiData === 'object' && apiData !== null) {
      // Format: { Beginner: {count: X}, Learner: {count: Y}, Advanced: {count: Z}, Expert: {count: W} }
      // or { beginner: X, learner: Y, advanced: Z, expert: W }
      // or just { Advanced: { ... } } as seen in Diagnosis
      chartDataValues = competencyLevels.map(level => {
        const levelKey = level.charAt(0).toUpperCase() + level.slice(1).toLowerCase() // Capitalize first letter
        const levelData = apiData[levelKey] || apiData[level.toLowerCase()]

        if (typeof levelData === 'object' && levelData !== null) {
          return levelData.count || levelData.value || levelData.students || 0
        } else {
          return levelData || 0
        }
      })
    } else if (Array.isArray(apiData)) {
      // Format: [{ level: 'Beginner', count: X }, { level: 'Learner', count: Y }, ...]
      chartDataValues = competencyLevels.map(level => {
        const levelData = apiData.find(item => item.level === level || item.competencyLevel === level)
        return levelData?.count || levelData?.value || 0
      })
    } else {
      console.log('Unknown competency API data format:', apiData)
      console.log('Available keys:', Object.keys(apiData))
      return null
    }

    const totalStudents = chartDataValues.reduce((sum, count) => sum + count, 0)

    if (totalStudents === 0) {
      console.log('No competency data in API response, returning null')
      return null
    }

    const labels = competencyLevels.map((level, index) => {
      return level
    })

    console.log('Competency chart data prepared:', { chartDataValues, labels, totalStudents })

    return {
      labels,
      datasets: [{
        data: chartDataValues,
        backgroundColor: colors,
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverBorderWidth: 2,
        hoverBorderColor: '#1a1a1a',
        // Highlight selected segment with darker border
        ...(selectedPieChart && {
          borderColor: colors.map((color, index) =>
            competencyLevels[index] === selectedPieChart.level ? '#1a1a1a' : '#ffffff'
          )
        })
      }]
    }
  }

  const prepareConsistencyChartData = () => {
    console.log('Preparing Consistency chart data from API...')
    console.log('Current chartData:', chartData)
    console.log('Assessment type for Consistency:', assessmentType)

    // Use API data if available - NO MOCK DATA
    if (!chartData || !chartData.consistencyData) {
      console.log('❌ No Consistency API data available - returning empty chart structure')
      return {
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderWidth: 0
        }]
      }
    }

    const consistencyApiData = chartData.consistencyData
    console.log('✅ Using real Consistency API data:', consistencyApiData)

    return prepareConsistencyDataFromResponse(consistencyApiData)
  }

  const prepareConsistencyDataFromResponse = (apiData) => {
    const labels = ['High Consistency', 'Moderate Consistency', 'Inconsistent']
    // Blue color scheme as originally requested
    const colors = ['#10B981', '#F59E0B', '#EF4444']

    // Extract data from API response - handle different possible response formats
    let chartDataValues = []

    if (apiData.data && Array.isArray(apiData.data)) {
      // Format: { data: [{ type: 'high', count: X }, ...] }
      const highData = apiData.data.find(item =>
        item.type === 'high' || item.type === 'highConsistency' || item.consistency === 'high'
      )
      const moderateData = apiData.data.find(item =>
        item.type === 'moderate' || item.type === 'moderateConsistency' || item.consistency === 'moderate'
      )
      const inconsistentData = apiData.data.find(item =>
        item.type === 'inconsistent' || item.type === 'low' || item.consistency === 'inconsistent'
      )

      chartDataValues = [
        highData?.count || highData?.value || highData?.students || 0,
        moderateData?.count || moderateData?.value || moderateData?.students || 0,
        inconsistentData?.count || inconsistentData?.value || inconsistentData?.students || 0
      ]
    } else if (apiData.highConsistency !== undefined) {
      // Format: { highConsistency: {count: X}, moderateConsistency: {count: Y}, inconsistency: {count: Z} }
      const highCount = (typeof apiData.highConsistency === 'object' ? apiData.highConsistency?.count : apiData.highConsistency) || 0
      const moderateCount = (typeof apiData.moderateConsistency === 'object' ? apiData.moderateConsistency?.count : apiData.moderateConsistency) || 0
      const inconsistentCount = (typeof apiData.inconsistency === 'object' ? apiData.inconsistency?.count : apiData.inconsistency) || 0

      console.log('🔍 Consistency data extraction debug:')
      console.log('- apiData.highConsistency:', apiData.highConsistency)
      console.log('- apiData.moderateConsistency:', apiData.moderateConsistency)
      console.log('- apiData.inconsistency:', apiData.inconsistency)
      console.log('- Extracted counts:', { highCount, moderateCount, inconsistentCount })

      chartDataValues = [highCount, moderateCount, inconsistentCount]
    } else if (apiData.high !== undefined) {
      // Format: { high: X, moderate: Y, inconsistent: Z }
      chartDataValues = [
        apiData.high || 0,
        apiData.moderate || 0,
        apiData.inconsistent || 0
      ]
    } else if (Array.isArray(apiData)) {
      // Format: [{ type: 'high', count: X }, { type: 'moderate', count: Y }, ...]
      const highData = apiData.find(item => item.type === 'high' || item.type === 'highConsistency')
      const moderateData = apiData.find(item => item.type === 'moderate' || item.type === 'moderateConsistency')
      const inconsistentData = apiData.find(item => item.type === 'inconsistent' || item.type === 'low')

      chartDataValues = [
        highData?.count || highData?.value || 0,
        moderateData?.count || moderateData?.value || 0,
        inconsistentData?.count || inconsistentData?.value || 0
      ]
    } else {
      console.log('Unknown consistency API data format:', apiData)
      return null
    }

    const totalStudents = chartDataValues.reduce((sum, count) => sum + count, 0)

    if (totalStudents === 0) {
      console.log('No consistency data in API response, returning null')
      return null
    }

    const chartLabels = labels.map((label, index) => {
      return label
    })

    console.log('Consistency chart data prepared from API:', { chartDataValues, chartLabels, totalStudents })

    return {
      labels: chartLabels,
      datasets: [{
        data: chartDataValues,
        backgroundColor: colors,
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverBorderWidth: 2,
        hoverBorderColor: '#1a1a1a',
        // Highlight selected segment with darker border
        ...(selectedConsistency && {
          borderColor: colors.map((color, index) =>
            ['high', 'moderate', 'inconsistent'][index] === selectedConsistency.type ? '#1a1a1a' : '#ffffff'
          )
        })
      }]
    }
  }

  const prepareAptitudeDataFromResponse = (apiData) => {
    console.log('Processing aptitude API data:', apiData)

    const labels = ['Beginner', 'Learner', 'Competent', 'Advanced']
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4']
    let chartDataValues = []
    let totalStudents = 0

    // Handle different possible API response formats
    if (apiData && apiData.data && Array.isArray(apiData.data)) {
      // Format: { data: [{ level: 'Beginner', count: X }, ...] }
      const beginnerData = apiData.data.find(item =>
        item.level === 'Beginner' || item.type === 'beginner' || item.aptitudeLevel === 'Beginner'
      )
      const learnerData = apiData.data.find(item =>
        item.level === 'Learner' || item.type === 'learner' || item.aptitudeLevel === 'Learner'
      )
      const competentData = apiData.data.find(item =>
        item.level === 'Competent' || item.type === 'competent' || item.aptitudeLevel === 'Competent'
      )
      const advancedData = apiData.data.find(item =>
        item.level === 'Advanced' || item.type === 'advanced' || item.aptitudeLevel === 'Advanced'
      )

      chartDataValues = [
        beginnerData?.count || beginnerData?.value || beginnerData?.students || 0,
        learnerData?.count || learnerData?.value || learnerData?.students || 0,
        competentData?.count || competentData?.value || competentData?.students || 0,
        advancedData?.count || advancedData?.value || advancedData?.students || 0
      ]
      totalStudents = apiData.totalCount || apiData.total || chartDataValues.reduce((sum, val) => sum + val, 0)
    } else if (apiData && apiData.Beginner !== undefined) {
      // Format: { Beginner: { count: X }, Learner: { count: Y }, ... }
      chartDataValues = [
        apiData.Beginner?.count || apiData.Beginner || 0,
        apiData.Learner?.count || apiData.Learner || 0,
        apiData.Competent?.count || apiData.Competent || 0,
        apiData.Advanced?.count || apiData.Advanced || 0
      ]
      totalStudents = apiData.totalCount || apiData.total || chartDataValues.reduce((sum, val) => sum + val, 0)
    } else if (Array.isArray(apiData)) {
      // Format: [{ level: 'Beginner', count: X }, ...]
      const beginnerItem = apiData.find(item => item.level === 'Beginner' || item.type === 'beginner')
      const learnerItem = apiData.find(item => item.level === 'Learner' || item.type === 'learner')
      const competentItem = apiData.find(item => item.level === 'Competent' || item.type === 'competent')
      const advancedItem = apiData.find(item => item.level === 'Advanced' || item.type === 'advanced')

      chartDataValues = [
        beginnerItem?.count || beginnerItem?.value || 0,
        learnerItem?.count || learnerItem?.value || 0,
        competentItem?.count || competentItem?.value || 0,
        advancedItem?.count || advancedItem?.value || 0
      ]
      totalStudents = chartDataValues.reduce((sum, val) => sum + val, 0)
    } else {
      console.log('Unknown aptitude API data format, using empty data')
      chartDataValues = [0, 0, 0, 0]
      totalStudents = 0
    }

    const chartLabels = labels.map((label, index) => {
      return label
    })

    console.log('Aptitude chart data prepared from API:', { chartDataValues, chartLabels, totalStudents })

    return {
      labels: chartLabels,
      datasets: [{
        data: chartDataValues,
        backgroundColor: colors,
        borderWidth: selectedAptitude ? 3 : 0,
        borderColor: colors.map((color, index) =>
          selectedAptitude && ['beginner', 'learner', 'competent', 'advanced'][index] === selectedAptitude.type ? '#000' : 'transparent'
        )
      }]
    }
  }

  const prepareAptitudeChartData = () => {
    console.log('Preparing Aptitude chart data from API...')
    console.log('Current chartData:', chartData)
    console.log('Assessment type for Aptitude:', assessmentType)

    // Use API data if available - NO MOCK DATA
    if (!chartData || !chartData.aptitudeData) {
      console.log('❌ No Aptitude API data available - returning empty chart structure')
      return {
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderWidth: 0
        }]
      }
    }

    const aptitudeApiData = chartData.aptitudeData
    console.log('✅ Using real Aptitude API data:', aptitudeApiData)

    return prepareAptitudeDataFromResponse(aptitudeApiData)
  }

  const prepareLineChartData = () => {
    console.log('Preparing Line chart data from API...')
    console.log('Current chartData:', chartData)
    console.log('Assessment type for Line chart:', assessmentType)

    // Use API data if available - NO MOCK DATA
    if (!chartData || (!chartData.aptitudeGroupsData && !chartData.communicationGroupsData)) {
      console.log('❌ No Line chart API data available - returning empty chart structure')
      return {
        labels: [],
        datasets: [{
          label: 'Assessment Scores',
          data: [],
          borderColor: '#1890ff',
          backgroundColor: 'rgba(24, 144, 255, 0.1)',
          borderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: [],
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          tension: 0.4
        }],
        fullData: [],
        visibleData: []
      }
    }

    // Use the appropriate data based on assessment type
    const lineApiData = isCommunicationAssessment ?
      chartData.communicationGroupsData :
      chartData.aptitudeGroupsData

    console.log('✅ Using real Line chart API data:', lineApiData)

    // If we have more than 5 items, we may want to start at the end to show most recent
    // This is handled in the prepareLineDataFromResponse function

    return prepareLineDataFromResponse(lineApiData)
  }

  const prepareLineDataFromResponse = (apiData) => {
    // Handle null or undefined API data
    if (!apiData) {
      console.log('❌ No line chart API data available - returning empty chart structure')
      return null
    }

    let assessmentData = []

    // Handle different possible API response formats
    if (Array.isArray(apiData)) {
      assessmentData = apiData
    } else if (apiData.data && Array.isArray(apiData.data)) {
      assessmentData = apiData.data
    } else if (apiData.assessments && Array.isArray(apiData.assessments)) {
      assessmentData = apiData.assessments
    } else {
      console.log('Unknown line chart API data format:', apiData)
      return null
    }

    // Map API data to chart format based on assessment type
    const chartData = assessmentData.map((item, index) => {
      if (isCommunicationAssessment) {
        // Communication assessment data format
        // Calculate total candidates from cefrGroups if available
        let candidateCount = item.totalStudents || item.student_count || item.candidates || item.count || 0

        // If candidateCount is still 0, try to calculate from cefrGroups
        if (candidateCount === 0 && item.cefrGroups && Array.isArray(item.cefrGroups)) {
          candidateCount = item.cefrGroups.reduce((total, group) => {
            return total + (group.students?.length || 0)
          }, 0)
        }

        return {
          assessmentName: item.isDiagnosis ? 'Diagnosis' : (item.assessmentName || item.name || item.title || `Assessment ${index + 1}`),
          score: item.overallAvgNormalizedScore || item.score || item.averageScore || 0,
          candidateCount: candidateCount,
          date: item.startTime || item.date || item.createdAt || new Date().toISOString().split('T')[0],
          assessmentId: item.assessmentInstituteMapId || item.id,
          attemptNumber: item.attemptNumber || null,
          isDiagnosis: item.isDiagnosis || false
        }
      } else {
        // Aptitude assessment data format
        // Calculate total candidates from difficultyGroups if available
        let candidateCount = item.total_students || item.candidateCount || item.candidates || item.student_count || 0

        // If candidateCount is still 0, try to calculate from difficultyGroups
        if (candidateCount === 0 && item.difficultyGroups && Array.isArray(item.difficultyGroups)) {
          candidateCount = item.difficultyGroups.reduce((total, group) => {
            return total + (group.students?.length || 0)
          }, 0)
        }

        return {
          assessmentName: item.isDiagnosis ? 'Diagnosis' : (item.assessment_name || item.assessmentName || item.name || `Assessment ${index + 1}`),
          score: item.score || item.averageScore || item.avg_score || 0,
          candidateCount: candidateCount,
          date: item.startTime || item.date || item.createdAt || new Date().toISOString().split('T')[0],
          assessmentId: item.assessment_institute_map_id || item.id,
          attemptNumber: item.attemptNumber || null,
          isDiagnosis: item.isDiagnosis || false
        }
      }
    })

    console.log('Line chart data prepared:', chartData)

    // Limit chart data to show only up to the currently selected assessment
    if (!isDiagnosis) {
      let currentIdx = -1
      // In attempt-order mode, match by attemptNumber
      if (attemptNumber && scheduleId) {
        currentIdx = chartData.findIndex(item => item.attemptNumber && item.attemptNumber === attemptNumber)
      }
      // Fallback: match by assessmentInstituteMapId
      if (currentIdx < 0) {
        const currentAssessmentId = assessment?.assessmentInstituteMapId ||
          assessment?.assessment_institute_map_id ||
          assessment?.assessmentInstituteMapID ||
          assessment?.id
        if (currentAssessmentId) {
          currentIdx = chartData.findIndex(item => String(item.assessmentId) === String(currentAssessmentId))
        }
      }
      if (currentIdx >= 0) {
        chartData.length = currentIdx + 1
      }
    }

    // Show only 5 items at a time
    const visibleData = chartData.slice(lineChartStartIndex, lineChartStartIndex + 5)

    // Generate labels: "Diagnosis" for diagnosis items, "Assmt. X" for regular assessments
    // Count how many diagnosis items exist before the visible window to adjust numbering
    const diagnosisCountBeforeWindow = chartData.slice(0, lineChartStartIndex).filter(item => item.isDiagnosis).length
    let regularAssessmentCounter = lineChartStartIndex - diagnosisCountBeforeWindow

    const labels = visibleData.map((item) => {
      if (item.isDiagnosis) {
        return 'Diagnosis'
      }
      regularAssessmentCounter++
      return `Assmt. ${regularAssessmentCounter}`
    })

    return {
      fullData: chartData,
      visibleData,
      labels: labels,
      datasets: [{
        label: 'Assessment Scores',
        data: visibleData.map(item => item.score),
        borderColor: '#34c759',
        backgroundColor: 'rgba(52, 199, 89, 0.12)',
        borderWidth: 2.5,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#ffffff',
        pointBorderColor: '#34c759',
        pointBorderWidth: 2,
        tension: 0.35,
        fill: true
      }]
    }
  }

  useEffect(() => {
    const lineData = prepareLineChartData()
    if (lineData && Array.isArray(lineData.fullData)) {
      const total = lineData.fullData.length
      const maxStart = Math.max(0, total - 5)
      setLineChartStartIndex(maxStart)
      setSelectedLinePoint(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, assessment?.assessmentInstituteMapId, assessment?.assessment_institute_map_id, assessment?.id, attemptNumber, scheduleId, isDiagnosis])

  const handleLineChartScroll = (direction) => {
    console.log('🔄 handleLineChartScroll called with direction:', direction)
    console.log('🔄 Current lineChartStartIndex:', lineChartStartIndex)

    const lineData = prepareLineChartData()
    console.log('🔄 lineData:', lineData)
    console.log('🔄 fullData length:', lineData?.fullData?.length)

    if (!lineData || !lineData.fullData) {
      console.log('❌ No lineData or fullData, returning early')
      return
    }

    const totalItems = lineData.fullData.length
    const maxStartIndex = Math.max(0, totalItems - 5)
    console.log('🔄 totalItems:', totalItems, 'maxStartIndex:', maxStartIndex)

    if (direction === 'next') {
      const newIndex = Math.min(lineChartStartIndex + 1, maxStartIndex)
      console.log('🔄 Going NEXT, new index will be:', newIndex)
      setLineChartStartIndex(newIndex)
    } else if (direction === 'prev') {
      const newIndex = Math.max(lineChartStartIndex - 1, 0)
      console.log('🔄 Going PREV, new index will be:', newIndex)
      setLineChartStartIndex(newIndex)
    }

    // Clear selection when scrolling
    setSelectedLinePoint(null)
    console.log('✅ handleLineChartScroll completed')
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    animation: {
      animateScale: true,
      animateRotate: true,
      duration: 400
    },
    layout: {
      padding: 0
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    },
    cutout: '60%',
    radius: '90%',
    onClick: (event, elements) => {
      console.log('🎯 Chart clicked! Elements:', elements.length)
      if (elements.length > 0) {
        const elementIndex = elements[0].index
        const chart = event.chart

        console.log('🎯 Chart canvas ID:', chart.canvas.id)

        // Determine which chart was clicked based on chart ID or canvas ID
        if (chart.canvas.id === 'cefr-chart') {
          console.log('🎯 CEFR chart detected, calling handler')
          handleCefrClick(event, elements)
        } else if (chart.canvas.id === 'consistency-chart') {
          console.log('🎯 Consistency chart detected, calling handler')
          handleConsistencyClick(event, elements)
        } else {
          console.log('🎯 Unknown chart clicked:', chart.canvas.id)
        }
      } else {
        console.log('🎯 Chart clicked but no elements found')
      }
    }
  }

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 8,
        right: 24,
        bottom: 8,
        left: 8
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false // Disable tooltips as per requirements
      }
    },
    onHover: (event, elements) => {
      console.log('🎯 Line chart hover detected:', elements.length > 0 ? 'Element found' : 'No element')
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.08)',
          borderDash: [4, 4],
          drawBorder: false,
          drawTicks: false
        },
        ticks: {
          font: {
            size: 13
          },
          maxRotation: 45,
          color: '#9aa0a6'
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.08)',
          borderDash: [4, 4],
          drawBorder: false,
          drawTicks: false
        },
        ticks: {
          font: {
            size: 11
          },
          color: '#9aa0a6',
          stepSize: 20
        },
        suggestedMax: 100
      }
    },
    onClick: (event, elements) => {
      console.log('🎯 Aptitude line chart click triggered!', { event, elements })

      if (elements.length > 0) {
        const elementIndex = elements[0].index
        const lineData = prepareLineChartData()

        if (lineData && lineData.visibleData && lineData.visibleData[elementIndex]) {
          const clickedPoint = lineData.visibleData[elementIndex]
          console.log('🎯 Line chart point clicked:', clickedPoint)

          setSelectedLinePoint({
            assessmentName: lineData.labels[elementIndex],
            score: clickedPoint.score,
            candidateCount: clickedPoint.candidateCount,
            date: clickedPoint.date
          })

          // Note: Not calling setChartFilters here to avoid triggering student list API
          // The line chart click is just for visual selection/display purposes
          console.log('🎯 Line chart point selected (no student list API call)')
        }
      } else {
        console.log('❌ No elements found in aptitude chart click')
      }
    }
  }

  // We'll let individual charts decide if they can render based on their data
  console.log('Proceeding to render charts...')

  // Use different chart data preparation based on assessment type
  const pieChartData = prepareCefrChartData()
  const consistencyChartData = prepareConsistencyChartData()
  const lineChartData = prepareLineChartData()

  console.log('Final chart data prepared:')
  console.log('pieChartData:', pieChartData)
  console.log('consistencyChartData:', consistencyChartData)
  console.log('lineChartData:', lineChartData)

  // Dynamic titles based on assessment type
  const chartTitles = {
    cefr: isCommunicationAssessment ? 'CEFR Level Distribution' : 'Competency Distribution',
    line: isCommunicationAssessment ? 'Candidate Progression Trends' : 'Candidate Progression Trends',
    quality: isCommunicationAssessment ? 'Candidate Consistency Trends' : 'Candidate Consistency Trends'
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header with Toggle and Clear Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        {/* Toggle Button for Hide/Show Charts */}
        <div
          onClick={() => setChartsVisible(!chartsVisible)}
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            fontSize: '20px',
            fontWeight: 'bold'
          }}
        >
          Click to View/Hide Graphs
          <span style={{ marginLeft: '8px' }}>
            {chartsVisible ? '▲' : '▼'}
          </span>
        </div>
      </div>

      {/* Duration Selector */}
      {/*
      {chartsVisible && (
        <DurationContainer>
          <DurationLabel>Select Duration</DurationLabel>
          <DurationSelect style={{minWidth: '230px'}} defaultValue="Until Today">
            <option value="Until Today">Until Today</option>
            <option value="Last 7 Days">Last 7 Days</option>
            <option value="Last 30 Days">Last 30 Days</option>
            <option value="Last 3 Months">Last 3 Months</option>
            <option value="Last 6 Months">Last 6 Months</option>
            <option value="Last Year">Last Year</option>
            <option value="Last 2 Years">Last 2 Years</option>
          </DurationSelect>
        </DurationContainer>
      )}
      */}

      {/* Charts Section - Only show if chartsVisible is true */}
      {chartsVisible && (
        <Row gutter={[24, 24]} justify="center" align="stretch">
          {/* CEFR Level Distribution */}
          <Col xs={24} lg={8} style={{ display: 'flex' }}>
            <ChartContainer>
              <ChartTitle>{chartTitles.cefr}</ChartTitle>
              <ChartWrapper>
                {chartLoading ? (
                  <Spin size="large" />
                ) : pieChartData && pieChartData.labels && pieChartData.labels.length > 0 ? (
                  <Doughnut
                    id="cefr-chart"
                    data={pieChartData}
                    options={chartOptions}
                  />
                ) : (
                  <BlurOverlayContainer>
                    <BlurredChartPlaceholder>
                      <Doughnut
                        data={{
                          labels: isCommunicationAssessment
                            ? ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
                            : ['Beginner', 'Learner', 'Competent', 'Advanced'],
                          datasets: [{
                            data: isCommunicationAssessment ? [15, 20, 25, 20, 12, 8] : [25, 30, 25, 20],
                            backgroundColor: isCommunicationAssessment
                              ? ['#1a48ae', '#4a6ae4', '#6c96f0', '#3d91ff', '#62a1d5', '#aedff7']
                              : ['#1a48ae', '#4a6ae4', '#6c96f0', '#3d91ff'],
                            borderWidth: 2,
                            borderColor: '#ffffff'
                          }]
                        }}
                        options={{ ...chartOptions, plugins: { ...chartOptions.plugins, legend: { display: false } } }}
                      />
                    </BlurredChartPlaceholder>
                    <DataNotAvailableOverlay>
                      <DataNotAvailableText>
                        Data not available
                      </DataNotAvailableText>
                    </DataNotAvailableOverlay>
                  </BlurOverlayContainer>
                )}
              </ChartWrapper>
              {/* Legend */}
              {pieChartData && pieChartData.labels && pieChartData.labels.length > 0 && (
                <LegendContainer>
                  {pieChartData.labels.map((label, index) => (
                    <div key={label} style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '14px',
                      color: '#666'
                    }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: pieChartData.datasets[0].backgroundColor[index],
                        marginRight: '4px',
                        borderRadius: '50%'
                      }}></div>
                      {isCommunicationAssessment ? getFriendlyCefr(label) : getFriendlyAptitude(label)}
                    </div>
                  ))}
                </LegendContainer>
              )}
              {/* Footer section with fixed height */}
              <ChartFooter>
                {/* Right-aligned clear link like in the design */}
                <div style={{ height: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                  {selectedPieChart && (
                    <ClearLink onClick={handleClearPieChartFilter}>Clear</ClearLink>
                  )}
                </div>
                {selectedPieChart ? (
                  <SelectedInfo>
                    Selected {isCommunicationAssessment ? getFriendlyCefr(selectedPieChart.level) : getFriendlyAptitude(selectedPieChart.level)} No. of Students: {selectedPieChart.count}
                  </SelectedInfo>
                ) : (
                  pieChartData && pieChartData.labels && pieChartData.labels.length > 0 ? (
                    <ChartNote>
                      <InfoIcon><b>!</b></InfoIcon>
                      <span><strong>Note:</strong> Click the graph for to show those set of candidates</span>
                    </ChartNote>
                  ) : <div style={{ minHeight: '44px' }} />
                )}
              </ChartFooter>
            </ChartContainer>
          </Col>

          {/* Candidate Progression Trends - Show for both Aptitude and Communication */}
          {!disableLineChart && (
            <Col xs={24} lg={8} style={{ display: 'flex' }}>
              <ChartContainer>
                <ChartTitle>{chartTitles.line}</ChartTitle>
                <LineChartContainer>
                  <ChartViewport>
                    {(() => {
                      if (chartLoading) {
                        return (
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Spin size="large" />
                          </div>
                        )
                      }
                      const lineData = prepareLineChartData()

                      if (!lineData || !lineData.visibleData || lineData.visibleData.length === 0) {
                        return (
                          <BlurOverlayContainer style={{ height: '280px' }}>
                            <BlurredChartPlaceholder>
                              <Line
                                data={{
                                  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
                                  datasets: [{
                                    label: 'Sample Data',
                                    data: [65, 72, 68, 75, 78],
                                    borderColor: '#34c759',
                                    backgroundColor: 'rgba(52, 199, 89, 0.1)',
                                    borderWidth: 2,
                                    pointRadius: 4,
                                    pointBackgroundColor: '#34c759',
                                    tension: 0.4,
                                    fill: true
                                  }]
                                }}
                                options={{ ...lineChartOptions, plugins: { ...lineChartOptions.plugins, legend: { display: false } } }}
                              />
                            </BlurredChartPlaceholder>
                            <DataNotAvailableOverlay>
                              <DataNotAvailableText>
                                📈 Data not available
                              </DataNotAvailableText>
                            </DataNotAvailableOverlay>
                          </BlurOverlayContainer>
                        )
                      }

                      return (
                        <Line
                          data={lineData}
                          options={lineChartOptions}
                          style={{ cursor: 'pointer' }}
                        />
                      )
                    })()
                    }
                  </ChartViewport>
                  {/* Scroll controls (placed below chart to avoid overlap) */}
                  {(() => {
                    const lineData = prepareLineChartData()
                    const canGoBack = lineChartStartIndex > 0
                    const canGoForward = lineData && lineData.fullData && lineChartStartIndex < lineData.fullData.length - 5

                    console.log('📍 Nav render - lineChartStartIndex:', lineChartStartIndex)
                    console.log('📍 Nav render - fullData length:', lineData?.fullData?.length)
                    console.log('📍 Nav render - canGoBack:', canGoBack, 'canGoForward:', canGoForward)

                    // Show navigation if there's data to scroll through (more than 5 items)
                    const hasNavigation = lineData && lineData.fullData && lineData.fullData.length > 5
                    if (!hasNavigation) {
                      console.log('📍 No navigation needed (5 or fewer items)')
                      return null
                    }

                    return (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '6px',
                        marginTop: '8px',
                        position: 'relative',
                        zIndex: 100
                      }}>
                        {/* Previous button */}
                        <button
                          type="button"
                          style={{
                            fontSize: '14px',
                            color: canGoBack ? '#5f6368' : '#ccc',
                            cursor: canGoBack ? 'pointer' : 'not-allowed',
                            fontWeight: 'bold',
                            lineHeight: 1,
                            userSelect: 'none',
                            background: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '4px 10px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                            opacity: canGoBack ? 1 : 0.5,
                            pointerEvents: 'auto',
                            position: 'relative',
                            zIndex: 101
                          }}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            console.log('⬅️ Prev button clicked, canGoBack:', canGoBack)
                            handleLineChartScroll('prev')
                          }}
                          title="Previous"
                        >
                          &lt;
                        </button>
                        {/* Next button */}
                        <button
                          type="button"
                          style={{
                            fontSize: '14px',
                            color: canGoForward ? '#5f6368' : '#ccc',
                            cursor: canGoForward ? 'pointer' : 'not-allowed',
                            fontWeight: 'bold',
                            lineHeight: 1,
                            userSelect: 'none',
                            background: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '4px 10px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                            opacity: canGoForward ? 1 : 0.5,
                            pointerEvents: 'auto',
                            position: 'relative',
                            zIndex: 101
                          }}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            console.log('➡️ Next button clicked, canGoForward:', canGoForward)
                            handleLineChartScroll('next')
                          }}
                          title="Next"
                          disabled={!canGoForward}
                        >
                          &gt;
                        </button>
                      </div>
                    )
                  })()}
                </LineChartContainer>
                {/* Legend */}
                {(() => {
                  const lineData = prepareLineChartData()
                  if (!(lineData && lineData.labels && lineData.labels.length > 0)) return null
                  return (
                    <LegendContainer>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '12px',
                        color: '#666'
                      }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          backgroundColor: '#34c759',
                          marginRight: '4px',
                          borderRadius: '50%'
                        }}></div>
                        Score Trend
                      </div>
                    </LegendContainer>
                  )
                })()}
                {/* Footer section with fixed height */}
                <ChartFooter>
                  <div style={{ height: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                    {selectedLinePoint && (
                      <ClearLink onClick={handleClearLinePointFilter}>Clear</ClearLink>
                    )}
                  </div>
                  {selectedLinePoint ? (
                    <SelectedInfo>
                      Selected {selectedLinePoint.assessmentName} Score: {selectedLinePoint.score} | Candidates: {selectedLinePoint.candidateCount}
                    </SelectedInfo>
                  ) : (
                    <BlueInfoBox>
                      <InfoIcon><b>!</b></InfoIcon>
                      <InfoText>
                        <strong>Note:</strong> Click the graph for to show those set of candidates
                      </InfoText>
                    </BlueInfoBox>
                  )}
                </ChartFooter>
              </ChartContainer>
            </Col>
          )}

          {/* Candidate Quality Trends (Consistency Analysis) */}
          <Col xs={24} lg={8} style={{ display: 'flex' }}>
            <ChartContainer>
              <ChartTitle>{chartTitles.quality}</ChartTitle>
              <ChartWrapper>
                {chartLoading ? (
                  <Spin size="large" />
                ) : consistencyChartData && consistencyChartData.labels && consistencyChartData.labels.length > 0 ? (
                  <Doughnut
                    id="consistency-chart"
                    data={consistencyChartData}
                    options={chartOptions}
                  />
                ) : (
                  <BlurOverlayContainer>
                    <BlurredChartPlaceholder>
                      <Doughnut
                        data={{
                          labels: ['High Consistency', 'Moderate Consistency', 'Inconsistent'],
                          datasets: [{
                            data: [40, 35, 25],
                            backgroundColor: ['#10B981', '#F59E0B', '#EF4444'],
                            borderWidth: 2,
                            borderColor: '#ffffff'
                          }]
                        }}
                        options={{ ...chartOptions, plugins: { ...chartOptions.plugins, legend: { display: false } } }}
                      />
                    </BlurredChartPlaceholder>
                    <DataNotAvailableOverlay>
                      <DataNotAvailableText>
                        📈 Data not available
                      </DataNotAvailableText>
                    </DataNotAvailableOverlay>
                  </BlurOverlayContainer>
                )}
              </ChartWrapper>
              {/* Legend */}
              {consistencyChartData && consistencyChartData.labels && consistencyChartData.labels.length > 0 && (
                <LegendContainer>
                  {consistencyChartData.labels.map((label, index) => (
                    <div key={label} style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: consistencyChartData.datasets[0].backgroundColor[index],
                        marginRight: '4px',
                        borderRadius: '50%'
                      }}></div>
                      {label}
                    </div>
                  ))}
                </LegendContainer>
              )}
              {/* Footer section with fixed height */}
              <ChartFooter>
                <div style={{ height: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                  {selectedConsistency && (
                    <ClearLink onClick={handleClearConsistencyFilter}>Clear</ClearLink>
                  )}
                </div>
                {selectedConsistency ? (
                  <SelectedInfo>
                    Selected {selectedConsistency.label || selectedConsistency.type} No. of Students: {selectedConsistency.count}
                  </SelectedInfo>
                ) : (
                  consistencyChartData && consistencyChartData.labels && consistencyChartData.labels.length > 0 ? (
                    <ChartNote>
                      <InfoIcon><b>!</b></InfoIcon>
                      <span><strong>Note:</strong> Click the graph for to show those set of candidates</span>
                    </ChartNote>
                  ) : <div style={{ minHeight: '44px' }} />
                )}
              </ChartFooter>
            </ChartContainer>
          </Col>
        </Row>
      )}
    </div>
  )
}

export default AssessmentCharts
